import { useAttrs, computed } from "vue";

/**
 * Splits a wrapper component's fallthrough attributes into two groups so a form
 * control can place each where it belongs:
 *
 *   - rootAttrs: `class` and `style` — layout the PARENT means for the
 *     component as a whole (grid `col-span-*`, margins, widths). These must land
 *     on the component's ROOT element, or grid/flex layout collapses.
 *   - controlAttrs: everything else — `tabindex`, `aria-*`, `@focus`/`@click`
 *     and other listeners — which belong on the inner input/select so the field
 *     is a single tab stop and its handlers fire from the real control.
 *
 * Use with `defineOptions({ inheritAttrs: false })`: bind `rootAttrs` on the
 * root element and `controlAttrs` on the inner control. Without this split,
 * binding all of `$attrs` to the inner control sends the parent's layout
 * classes to the wrong element (the classic "every field is compressed" bug),
 * while binding them to the root reintroduces the double-tab-stop bug.
 */
export function useControlAttrs() {
  const attrs = useAttrs();

  const rootAttrs = computed(() => {
    const out = {};
    if (attrs.class !== undefined) out.class = attrs.class;
    if (attrs.style !== undefined) out.style = attrs.style;
    return out;
  });

  const controlAttrs = computed(() => {
    // eslint-disable-next-line no-unused-vars
    const { class: _class, style: _style, ...rest } = attrs;
    return rest;
  });

  return { rootAttrs, controlAttrs };
}
